import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Empty,
  Grid,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Slider,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  ShoppingCartOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  AppstoreOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  GiftOutlined,
  KeyOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import apiClient from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { useCartStore } from '../../stores/cart'

// ── Types ────────────────────────────────────────────────────────────────────

interface Product {
  id: number
  slug: string
  name: string
  description?: string
  kind: string
  provisioning_module?: string | null
  group_id?: number | null
  currency: string
  setup_fee: string
  default_cycle: string
  pricing: Record<string, string | number | { amount: string; currency?: string }>
  trial_days?: number
  minimum_term_months?: number
  cancellation_notice_days?: number
  active: boolean
}

interface ProductGroup {
  id: number
  slug: string
  name: string
  description?: string
  parent_id?: number | null
  sort_order: number
}

interface ProductFeature {
  id: number
  label: string
  icon?: string | null
  sort_order: number
}

interface OptionValue {
  id: number
  value: string
  label: string
  price_modifier: string
  price_modifier_type: 'fixed' | 'percent'
  provisioning_value?: string | null
  is_default: boolean
}

interface ProductOption {
  id: number
  code: string
  label: string
  kind: string
  required: boolean
  sort_order: number
  price_monthly: string
  price_setup: string
  is_active: boolean
  is_default: boolean
  help_text?: string | null
  display_format?: string | null
  values?: OptionValue[]
  config?: {
    min?: number
    max?: number
    step?: number
    default?: number | string | boolean
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// cycleLabels are resolved via t('billingCycle.*') at render time

function pricingEntries(p: Product): { cycle: string; price: string }[] {
  return Object.entries(p.pricing || {}).map(([cycle, v]) => {
    let price = ''
    if (typeof v === 'string') price = v
    else if (typeof v === 'number') price = v.toFixed(2)
    else if (v && typeof v === 'object' && 'amount' in v) price = v.amount
    return { cycle, price }
  }).filter(e => e.price)
}

function fmtPrice(v: string | number, currency: string) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return `${n.toFixed(2)} ${currency}`
}

// Compute the price modifier for the current config value of one option
// graduatedCharge mirrors the backend products.MeteredCharge: sum band×price
// across graduated tiers (up_to_gb upper bounds) for a quantity.
function graduatedCharge(tiers: { up_to_gb: number | null; price_per_gb: number }[], qty: number): number {
  if (!tiers.length || qty <= 0) return 0
  let lower = 0, total = 0
  for (const t of tiers) {
    const upper = t.up_to_gb ?? Number.POSITIVE_INFINITY
    const hi = Math.min(qty, upper)
    const band = hi - lower
    if (band > 0) total += band * t.price_per_gb
    lower = upper
    if (qty <= upper) break
  }
  return total
}

// tierUnitLabel maps a tier_quantity_key (max_mailboxes, max_nodes, …) to a
// German singular/plural label so the shop reads naturally for any metered
// quantity, not just mailboxes.
function tierUnitLabel(key: string): { one: string; many: string } {
  switch (key) {
    case 'max_mailboxes': return { one: 'Postfach', many: 'Postfächer' }
    case 'max_nodes': return { one: 'Node', many: 'Nodes' }
    case 'max_instances': return { one: 'Instanz', many: 'Instanzen' }
    case 'max_domains': return { one: 'Domain', many: 'Domains' }
    case 'max_accounts': return { one: 'Konto', many: 'Konten' }
    default: return { one: 'Einheit', many: 'Einheiten' }
  }
}

function optionPriceModifier(opt: ProductOption, value: unknown, basePrice: number): number {
  if (opt.kind === 'boolean') {
    return value ? parseFloat(opt.price_monthly || '0') : 0
  }
  if (opt.kind === 'select' && opt.values?.length) {
    const selected = opt.values.find(v => v.value === value)
    if (!selected) return 0
    const mod = parseFloat(selected.price_modifier || '0')
    if (selected.price_modifier_type === 'percent') return basePrice * mod / 100
    return mod
  }
  if ((opt.kind === 'number' || opt.kind === 'slider') && typeof value === 'number') {
    return value * parseFloat(opt.price_monthly || '0')
  }
  return parseFloat(opt.price_monthly || '0')
}

function formatDisplayValue(value: unknown, format?: string | null): string {
  if (!format) return String(value ?? '')
  return format.replace('{value}', String(value ?? ''))
}

function buildDefaultConfig(opts: ProductOption[]): Record<string, unknown> {
  const cfg: Record<string, unknown> = {}
  for (const opt of opts) {
    if (opt.kind === 'boolean') cfg[opt.code] = opt.is_default
    else if (opt.kind === 'select') {
      const def = opt.values?.find(v => v.is_default)
      if (def) cfg[opt.code] = def.value
    } else if (opt.kind === 'number' || opt.kind === 'slider') {
      cfg[opt.code] = opt.config?.min ?? opt.config?.default ?? 0
    }
  }
  return cfg
}

// ProductDescription renders a plain-text product description with light
// structure: lines ending in ":" become sub-headings, "- "/"• " lines become a
// check-marked bullet list (with the part before a ": " emphasised), blank
// lines add spacing. Keeps admins' free-text input but makes it look designed.
function ProductDescription({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let bullets: string[] = []
  const flush = (key: string) => {
    if (!bullets.length) return
    const items = bullets
    blocks.push(
      <ul key={key} style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
        {items.map((b, j) => {
          const ci = b.indexOf(': ')
          const lead = ci > 0 && ci < 42 ? b.slice(0, ci) : ''
          const rest = lead ? b.slice(ci + 2) : b
          return (
            <li key={j} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13, lineHeight: 1.5, color: 'var(--nc-text, #374151)' }}>
              <CheckCircleOutlined style={{ color: '#10b981', marginTop: 3, flexShrink: 0 }} />
              <span>{lead && <strong style={{ color: 'var(--nc-text-strong, #1f2937)' }}>{lead}: </strong>}{rest}</span>
            </li>
          )
        })}
      </ul>,
    )
    bullets = []
  }
  lines.forEach((raw, i) => {
    const line = raw.trim()
    if (!line) { flush(`u${i}`); return }
    if (line.startsWith('- ') || line.startsWith('• ')) { bullets.push(line.slice(2).trim()); return }
    flush(`u${i}`)
    if (line.endsWith(':') && line.length <= 60) {
      blocks.push(
        <div key={i} style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-strong, #1f2937)', margin: '4px 0 6px' }}>
          {line.replace(/:$/, '')}
        </div>,
      )
    } else {
      blocks.push(
        <p key={i} style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--nc-text-secondary, #64748b)', margin: '0 0 10px' }}>
          {line}
        </p>,
      )
    }
  })
  flush('uend')
  return <div>{blocks}</div>
}

// ── ProductCard ───────────────────────────────────────────────────────────────

function ProductCard({ p, onOrder, catalogBase = '/api/v1/portal' }: { p: Product; onOrder: (p: Product) => void; catalogBase?: string }) {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'de').split('-')[0]
  const entries = pricingEntries(p)
  const lowestPrice = entries.reduce((min, e) =>
    parseFloat(e.price) < parseFloat(min.price) ? e : min, entries[0] ?? { price: '0', cycle: '' })

  const featuresQ = useQuery({
    queryKey: ['portal-product-features', p.id, lang],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ProductFeature[] }>(
        `${catalogBase}/catalog/products/${p.id}/features?lang=${lang}`)
      return res.data.data || []
    },
    staleTime: 5 * 60 * 1000,
  })

  const features = featuresQ.data || []

  return (
    <Card
      className="content-card h-full"
      style={{ display: 'flex', flexDirection: 'column' }}
      styles={{ body: { display: 'flex', flexDirection: 'column', flex: 1 } }}
    >
      <div className="flex-1">
        <div className="flex-between mb-8" style={{ alignItems: 'flex-start' }}>
          <Typography.Title level={5} style={{ margin: 0 }}>{p.name}</Typography.Title>
          <Space size={4} style={{ flexShrink: 0, marginLeft: 8 }}>
            {(p.trial_days || 0) > 0 && (
              <Tag color="success">
                {t('portal.shop.trialBadge', '{{n}} Tage gratis', { n: p.trial_days })}
              </Tag>
            )}
            {(p.minimum_term_months || 0) > 0 && (
              <Tag color="blue">
                {p.minimum_term_months! >= 12
                  ? t('portalShop.termYears', '{{n}} Jahr(e) Laufzeit', { n: p.minimum_term_months! / 12 })
                  : t('portalShop.termMonths', '{{n}} Mon. Laufzeit', { n: p.minimum_term_months })}
              </Tag>
            )}
          </Space>
        </div>

        {p.description && (
          <div style={{ marginBottom: 12 }}>
            <ProductDescription text={p.description} />
          </div>
        )}

        {features.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', fontSize: 13 }}>
            {features.slice(0, 5).map(f => (
              <li key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, color: '#374151' }}>
                <span style={{ color: '#10b981', flexShrink: 0 }}>{f.icon || '✓'}</span>
                {f.label}
              </li>
            ))}
            {features.length > 5 && (
              <li style={{ color: '#64748b', fontSize: 12 }}>+{features.length - 5} {t('portalShop.moreFeatures', 'weitere')}</li>
            )}
          </ul>
        )}
      </div>

      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginTop: 'auto' }}>
        {entries.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {entries.length > 1 ? t('portalShop.priceFrom', 'ab') : t(`billingCycle.${lowestPrice.cycle}`, lowestPrice.cycle)}
            </Typography.Text>
            <div>
              <Typography.Text strong style={{ fontSize: 20 }}>
                {parseFloat(lowestPrice.price).toFixed(2)}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 13, marginLeft: 4 }}>
                {p.currency} / {t(`billingCycle.${lowestPrice.cycle}`, lowestPrice.cycle)}
              </Typography.Text>
            </div>
            {p.setup_fee && Number(p.setup_fee) > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                + {p.setup_fee} {p.currency} {t('portal.shop.setupFee', 'Einrichtung')}
              </Typography.Text>
            )}
          </div>
        )}
        <Button type="primary" block onClick={() => onOrder(p)}>
          {t('portal.shop.order', 'Jetzt bestellen')}
        </Button>
      </div>
    </Card>
  )
}

// ── ProductGrid ───────────────────────────────────────────────────────────────

function ProductGrid({ products, onOrder, catalogBase }: { products: Product[]; onOrder: (p: Product) => void; catalogBase?: string }) {
  const { t } = useTranslation()
  if (products.length === 0) return <Empty description={t('portalShop.noProducts', 'Keine Produkte in dieser Kategorie.')} />
  return (
    <Row gutter={[16, 16]}>
      {products.map(p => (
        <Col key={p.id} xs={24} sm={12} lg={8}>
          <ProductCard p={p} onOrder={onOrder} catalogBase={catalogBase} />
        </Col>
      ))}
    </Row>
  )
}

// ── Main shop page ────────────────────────────────────────────────────────────

export default function PortalShop() {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'de').split('-')[0]
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeGroup, setActiveGroup] = useState<number | 'all'>('all')

  // Öffentliche Route /shop nutzt den public API-Prefix (kein Auth nötig).
  // Portal-Route /portal/shop nutzt den authentifizierten Prefix.
  const isPublicShop = !window.location.pathname.startsWith('/portal')
  const catalogBase = isPublicShop ? '/api/v1/public' : '/api/v1/portal'
  const shopRoot = isPublicShop ? '/shop' : '/portal/shop'
  // Produkt anklicken → eigene Konfigurationsseite (slug-basiert, teilbar).
  const goToProduct = (p: Product) => navigate(`${shopRoot}/${p.slug}`)

  const listQ = useQuery({
    queryKey: ['catalog-products', catalogBase, lang],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Product[] }>(`${catalogBase}/catalog/products?lang=${lang}`)
      return res.data.data || []
    },
  })

  // ?product=<slug> — direkt zur Konfigurationsseite des Produkts.
  useEffect(() => {
    const slugParam = searchParams.get('product')
    if (!slugParam || !listQ.data) return
    const target = listQ.data.find(p => p.slug === slugParam)
    if (target) navigate(`${shopRoot}/${target.slug}`, { replace: true })
  }, [searchParams, listQ.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const groupsQ = useQuery({
    queryKey: ['catalog-groups', catalogBase],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ProductGroup[] }>(`${catalogBase}/catalog/groups`)
      return res.data.data || []
    },
  })

  const products = listQ.data || []
  const groups = groupsQ.data || []

  const withTrial = products.filter(p => (p.trial_days || 0) > 0)

  const grouped = useMemo(() => {
    const m = new Map<number | null, Product[]>()
    for (const p of products) {
      const key = p.group_id ?? null
      if (!m.has(key)) m.set(key, [])
      m.get(key)!.push(p)
    }
    return m
  }, [products])

  const filtered = useMemo(() => {
    if (activeGroup === 'all') return products
    return products.filter(p => p.group_id === activeGroup)
  }, [products, activeGroup])

  const ungrouped = grouped.get(null) || []

  // ── Nested catalog tree ────────────────────────────────────────────────────
  const childrenOf = useMemo(() => {
    const m = new Map<number, ProductGroup[]>()
    for (const g of groups) {
      if (g.parent_id) {
        if (!m.has(g.parent_id)) m.set(g.parent_id, [])
        m.get(g.parent_id)!.push(g)
      }
    }
    return m
  }, [groups])

  // Top-level groups (no parent, or parent that no longer exists).
  const topGroups = useMemo(() => {
    const ids = new Set(groups.map(g => g.id))
    return groups.filter(g => !g.parent_id || !ids.has(g.parent_id))
  }, [groups])

  // Product count for a group including all of its descendants.
  const subtreeCount = useMemo(() => {
    const memo = new Map<number, number>()
    const calc = (id: number): number => {
      const cached = memo.get(id)
      if (cached !== undefined) return cached
      let n = (grouped.get(id) || []).length
      for (const c of (childrenOf.get(id) || [])) n += calc(c.id)
      memo.set(id, n)
      return n
    }
    return calc
  }, [grouped, childrenOf])

  const groupIds = useMemo(() => new Set(groups.map(g => g.id)), [groups])
  const isTop = (g: ProductGroup) => !g.parent_id || !groupIds.has(g.parent_id)
  const parentOf = (g: ProductGroup) => groups.find(x => x.id === g.parent_id)

  const selectedGroup = activeGroup === 'all' ? undefined : groups.find(g => g.id === activeGroup)
  // The top-level category currently in focus: the selected group if it is a
  // top group, otherwise the parent of the selected sub-group. Drives the
  // second-row sub-filter and which section is shown.
  const currentTop = selectedGroup ? (isTop(selectedGroup) ? selectedGroup : parentOf(selectedGroup)) : undefined

  // Body of a group: its own products, then its sub-groups as a collapsible
  // accordion (recursive). `expand` controls whether the sub-groups start open
  // — open when the user has drilled into this category, closed in the
  // "Alle"-overview so sub-groups only appear after a click.
  const renderGroupBody = (g: ProductGroup, expand: boolean): ReactNode => {
    const own = grouped.get(g.id) || []
    const kids = (childrenOf.get(g.id) || []).filter(k => subtreeCount(k.id) > 0)
    return (
      <>
        {own.length > 0 && <ProductGrid products={own} onOrder={goToProduct} catalogBase={catalogBase} />}
        {kids.length > 0 && (
          <Collapse
            // Re-mount on selection change so defaultActiveKey re-applies.
            key={`${activeGroup}-${g.id}`}
            className="shop-subgroup-collapse"
            bordered={false}
            defaultActiveKey={expand ? kids.map(k => String(k.id)) : []}
            items={kids.map(k => ({
              key: String(k.id),
              label: (
                <span className="shop-subgroup-label">
                  <ApartmentOutlined style={{ marginRight: 8, opacity: 0.6 }} />
                  {k.name}
                  <Tag style={{ marginLeft: 8 }}>{subtreeCount(k.id)}</Tag>
                  {k.description && (
                    <Typography.Text type="secondary" style={{ marginLeft: 10, fontWeight: 400, whiteSpace: 'pre-wrap' }}>
                      {k.description}
                    </Typography.Text>
                  )}
                </span>
              ),
              children: renderGroupBody(k, true),
            }))}
          />
        )}
      </>
    )
  }

  const renderSection = (g: ProductGroup, expand: boolean): ReactNode => {
    if (subtreeCount(g.id) === 0) return null
    return (
      <div key={g.id} className="shop-group-section" id={`shop-group-${g.id}`}>
        <div className="shop-group-header">
          <AppstoreOutlined className="shop-group-icon" />
          <span className="shop-group-name">{g.name}</span>
          {g.description && (
            <Typography.Text type="secondary" className="shop-group-desc" style={{ whiteSpace: 'pre-wrap' }}>
              {g.description}
            </Typography.Text>
          )}
        </div>
        {renderGroupBody(g, expand)}
      </div>
    )
  }

  return (
    <>
      <PageHeader
        icon={<ShoppingCartOutlined />}
        title={t('portal.shop.title', 'Shop')}
        subtitle={t('portal.shop.subtitle', 'Wählen Sie ein Produkt und bestellen Sie direkt.')}
      />

      {/* Domain-Kategorie: eigener Such-/Bestellflow (Verfügbarkeit, Registrierung, Transfer) */}
      <Card
        className="content-card mb-16"
        styles={{ body: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 } }}
      >
        <Space>
          <GlobalOutlined style={{ fontSize: 28, color: '#2563EB' }} />
          <div>
            <Typography.Text strong style={{ display: 'block' }}>
              {t('portal.shop.domainTitle', 'Domain registrieren oder transferieren')}
            </Typography.Text>
            <Typography.Text type="secondary" className="text-sm">
              {t('portal.shop.domainSub', 'Wunschdomain prüfen, registrieren oder mit Auth-Code umziehen.')}
            </Typography.Text>
          </div>
        </Space>
        <Button type="primary" icon={<SearchOutlined />}
          onClick={() => navigate(window.location.pathname.startsWith('/portal') ? '/portal/shop/domains' : '/shop/domains')}>
          {t('portal.shop.domainSearch', 'Domain suchen')}
        </Button>
      </Card>

      {withTrial.length > 0 && (
        <Card className="content-card mb-16"
          style={{ background: 'linear-gradient(135deg, #2563eb 0%, #143852 100%)', border: 'none' }}>
          <Typography.Text strong style={{ color: '#fff', fontSize: 15 }}>
            🎉 {t('portal.shop.trialPromo', 'Kostenlos testen:')}
          </Typography.Text>
          <Space wrap style={{ marginTop: 8 }}>
            {withTrial.map(p => (
              <Button key={p.id} size="small" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}
                onClick={() => goToProduct(p)}>
                {p.name} — {t('portalShop.trialDays', '{{n}} Tage gratis', { n: p.trial_days })}
              </Button>
            ))}
          </Space>
        </Card>
      )}

      {topGroups.length > 1 && (
        <>
          {/* Row 1: top-level categories only — sub-groups appear after a click. */}
          <div className="shop-group-filter">
            <Button
              type={activeGroup === 'all' ? 'primary' : 'default'}
              size="small"
              icon={<AppstoreOutlined />}
              onClick={() => setActiveGroup('all')}
            >
              {t('portal.shop.allProducts', 'Alle')} ({products.length})
            </Button>
            {topGroups.map(g => {
              const cnt = subtreeCount(g.id)
              if (cnt === 0) return null
              const hasKids = (childrenOf.get(g.id) || []).some(k => subtreeCount(k.id) > 0)
              return (
                <Button key={g.id} size="small"
                  type={currentTop?.id === g.id ? 'primary' : 'default'}
                  onClick={() => setActiveGroup(g.id)}>
                  {g.name} ({cnt}){hasKids ? ' ▾' : ''}
                </Button>
              )
            })}
          </div>
          {/* Row 2: sub-categories of the focused top group (only once drilled in). */}
          {currentTop && (childrenOf.get(currentTop.id) || []).some(k => subtreeCount(k.id) > 0) && (
            <div className="shop-group-filter shop-group-subfilter">
              <Button size="small"
                type={activeGroup === currentTop.id ? 'primary' : 'default'}
                onClick={() => setActiveGroup(currentTop.id)}>
                {t('portal.shop.allIn', 'Alle in {{name}}', { name: currentTop.name })}
              </Button>
              {(childrenOf.get(currentTop.id) || []).map(k => {
                const cnt = subtreeCount(k.id)
                if (cnt === 0) return null
                return (
                  <Button key={k.id} size="small"
                    icon={<ApartmentOutlined />}
                    type={activeGroup === k.id ? 'primary' : 'default'}
                    onClick={() => setActiveGroup(k.id)}>
                    {k.name} ({cnt})
                  </Button>
                )
              })}
            </div>
          )}
        </>
      )}

      {groups.length > 0 ? (
        <>
          {activeGroup === 'all'
            ? topGroups.map(g => renderSection(g, false))
            : selectedGroup
              ? renderSection(selectedGroup, true)
              : null}
          {(activeGroup === 'all' && ungrouped.length > 0) && (
            <div className="shop-group-section">
              <div className="shop-group-header">
                <span className="shop-group-name">{t('portal.shop.otherProducts', 'Weitere Produkte')}</span>
              </div>
              <ProductGrid products={ungrouped} onOrder={goToProduct} catalogBase={catalogBase} />
            </div>
          )}
        </>
      ) : (
        <ProductGrid products={filtered} onOrder={goToProduct} catalogBase={catalogBase} />
      )}
    </>
  )
}

// ── ShopProductPage — dedicated configuration page (route /shop/p/:id) ──────────

export function ShopProductPage() {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'de').split('-')[0]
  const navigate = useNavigate()
  const { productSlug } = useParams<{ productSlug: string }>()
  const isPublicShop = !window.location.pathname.startsWith('/portal')
  const catalogBase = isPublicShop ? '/api/v1/public' : '/api/v1/portal'
  const shopRoot = isPublicShop ? '/shop' : '/portal/shop'

  const listQ = useQuery({
    queryKey: ['catalog-products', catalogBase, lang],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Product[] }>(`${catalogBase}/catalog/products?lang=${lang}`)
      return res.data.data || []
    },
  })

  // Accept either the slug (nice shareable links) or the numeric id.
  const product = (listQ.data || []).find(p => p.slug === productSlug || String(p.id) === String(productSlug))

  if (listQ.isLoading) {
    return <Card className="content-card" loading />
  }
  if (!product) {
    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(shopRoot)}>
            {t('portal.shop.backToShop', 'Zurück zum Shop')}
          </Button>
        </div>
        <Card className="content-card">
          <Empty description={t('portal.shop.productNotFound', 'Produkt nicht gefunden.')} />
        </Card>
      </>
    )
  }
  return <ProductConfigurator product={product} onBack={() => navigate(shopRoot)} />
}

// ── ProductConfigurator (rendered full-page by ShopProductPage) ─────────────────

function ProductConfigurator({ product, onBack }: { product: Product; onBack: () => void }) {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'de').split('-')[0]
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const screens = Grid.useBreakpoint()
  const addItem = useCartStore((s) => s.addItem)

  const [cycle, setCycle] = useState<string>('')
  const [quantity, setQuantity] = useState(1)
  const [couponCode, setCouponCode] = useState('')
  const [couponApplied, setCouponApplied] = useState<{ discount: string; code: string } | null>(null)
  const [config, setConfig] = useState<Record<string, unknown>>({})
  // Inklusiv-Domains, die der Kunde beim Bestellen erfasst (register/transfer).
  const [inclDomains, setInclDomains] = useState<{ domain: string; action: 'register' | 'transfer' | 'own'; auth_code: string; available?: boolean | null; checking?: boolean }[]>([])
  // Live availability check against the public domain-search API, right in the
  // order flow: register → must be free, transfer → must already be registered.
  // "own" (customer manages the domain elsewhere) needs no check.
  const checkInclDomain = async (i: number) => {
    if (inclDomains[i]?.action === 'own') return
    const dom = (inclDomains[i]?.domain || '').trim().toLowerCase()
    if (!dom || !dom.includes('.')) return
    setInclDomains(arr => arr.map((x, j) => j === i ? { ...x, checking: true, available: null } : x))
    try {
      const res = await apiClient.get<{ data: { items: { domain: string; available: boolean }[] } }>(
        `/api/v1/public/domains/search?q=${encodeURIComponent(dom)}`)
      const items = res.data.data.items || []
      const hit = items.find(it => it.domain.toLowerCase() === dom) || items[0]
      setInclDomains(arr => arr.map((x, j) => j === i ? { ...x, checking: false, available: hit ? hit.available : null } : x))
    } catch {
      setInclDomains(arr => arr.map((x, j) => j === i ? { ...x, checking: false, available: null } : x))
    }
  }

  const isPortal = window.location.pathname.startsWith('/portal')
  const catalogApiBase = isPortal ? '/api/v1/portal' : '/api/v1/public'

  // Logged-in customer (portal) → resolve the exact tax situation
  // (reverse-charge / Kleinunternehmer / domestic). Public shop falls back
  // to the product's domestic rate.
  const meQ = useQuery({
    queryKey: ['portal-me-customer'],
    queryFn: async () =>
      (await apiClient.get<{ data: { customer_id: number } }>('/api/v1/portal/me')).data.data,
    enabled: isPortal,
    staleTime: 5 * 60_000,
  })
  const taxQ = useQuery({
    queryKey: ['shop-tax-preview', product?.id, meQ.data?.customer_id],
    queryFn: async () => {
      const params = new URLSearchParams({ product_id: String(product!.id) })
      if (meQ.data?.customer_id) params.set('customer_id', String(meQ.data.customer_id))
      const res = await apiClient.get<{ data: { rate_pct: string; reason: string; note?: string } }>(
        `/api/v1/public/tax-preview?${params.toString()}`)
      return res.data.data
    },
    enabled: !!product && (!isPortal || meQ.isFetched),
  })

  const optionsQ = useQuery({
    queryKey: ['catalog-options', product?.id, catalogApiBase],
    queryFn: async () => {
      if (!product) return []
      const res = await apiClient.get<{ data: ProductOption[] }>(
        `${catalogApiBase}/catalog/products/${product.id}/options`)
      return res.data.data || []
    },
    enabled: !!product,
  })

  const featuresQ = useQuery({
    queryKey: ['catalog-features', product?.id, catalogApiBase, lang],
    queryFn: async () => {
      if (!product) return []
      const res = await apiClient.get<{ data: ProductFeature[] }>(
        `${catalogApiBase}/catalog/products/${product.id}/features?lang=${lang}`)
      return res.data.data || []
    },
    enabled: !!product,
  })

  const inclQ = useQuery({
    queryKey: ['catalog-inclusive', product?.id, catalogApiBase],
    queryFn: async () => {
      if (!product) return { count: 0, tlds: [] as string[] }
      const res = await apiClient.get<{ data: { count: number; tlds: string[] } }>(
        `${catalogApiBase}/catalog/products/${product.id}/inclusive-domains`)
      return res.data.data || { count: 0, tlds: [] }
    },
    enabled: !!product,
  })
  const inclCount = inclQ.data?.count || 0
  const inclTlds = inclQ.data?.tlds || []

  // Object-storage (Impossible Cloud): the customer picks the bucket region
  // at order time. The choice lands in config.region (adapter reads it).
  const isS3 = product?.provisioning_module === 'impossiblecloud'
  const regionsQ = useQuery({
    queryKey: ['s3-regions', catalogApiBase],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { value: string; label: string }[] }>(
        `${catalogApiBase}/catalog/s3-regions`)
      return res.data.data || []
    },
    enabled: isS3,
  })
  const s3Regions = regionsQ.data || []

  // Antispam / Mail-Gateway: capture the domain to protect + the destination
  // mail server (transport) at order time — the provisioning Create needs them.
  const isMailgw = product?.provisioning_module === 'mailgateway'

  // Graduated quantity pricing (e.g. MailGuard licenses): the product names a
  // tier_quantity_key (e.g. max_mailboxes); the customer picks the quantity, the
  // price comes from the graduated tiers, and the value is sent as config[key]
  // to the license provisioner.
  const mbxPriceQ = useQuery({
    queryKey: ['tier-pricing', product?.id, catalogApiBase],
    queryFn: async () => {
      if (!product) return { enabled: false, quantity_key: '', tiers: [] as { up_to_gb: number | null; price_per_gb: number }[], metered: false }
      const res = await apiClient.get<{ data: { enabled: boolean; quantity_key: string; tiers: { up_to_gb: number | null; price_per_gb: number }[]; metered?: boolean } }>(
        `${catalogApiBase}/catalog/products/${product.id}/mailbox-pricing`)
      return res.data.data || { enabled: false, quantity_key: '', tiers: [], metered: false }
    },
    enabled: !!product,
  })
  const isMailboxPricing = !!mbxPriceQ.data?.enabled
  const isMeteredMailboxes = !!mbxPriceQ.data?.metered
  const tierKey = mbxPriceQ.data?.quantity_key || 'max_mailboxes'
  const mbxTiers = mbxPriceQ.data?.tiers || []

  // Default the quantity to 1 once we know the product uses tier pricing.
  useEffect(() => {
    if (isMailboxPricing && !config[tierKey]) {
      setConfig(prev => ({ ...prev, [tierKey]: 1 }))
    }
  }, [isMailboxPricing, tierKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const opts = optionsQ.data || []
  const features = featuresQ.data || []

  // Set default values when options load (preserve a chosen S3 region).
  useEffect(() => {
    if (opts.length > 0) {
      setConfig(prev => ({ ...buildDefaultConfig(opts), ...(prev.region ? { region: prev.region } : {}) }))
    }
  }, [opts])

  // Reset state when product changes
  useEffect(() => {
    if (product) {
      setCycle(product.default_cycle || Object.keys(product.pricing)[0] || '')
      setQuantity(1)
      setCouponCode('')
      setCouponApplied(null)
      setConfig(
        product.kind === 'ssl' ? { dcv_method: 'email' }
          : product.provisioning_module === 'impossiblecloud' ? { region: 'eu-central-2' }
            : product.provisioning_module === 'mailgateway' ? { transport_port: 25, transport_tls: 'may' }
              : {})
      setInclDomains([])
    }
  }, [product?.id])

  // Hosting needs a domain → show one domain row by default (once the inclusive
  // config is loaded) so the customer must actively pick register/transfer/own.
  useEffect(() => {
    if (product?.kind === 'hosting' && inclCount > 0) {
      setInclDomains(prev => prev.length === 0 ? [{ domain: '', action: 'register', auth_code: '' }] : prev)
    }
  }, [product?.id, inclCount])

  // For hosting, ordering is blocked until a domain is chosen (register/transfer/own).
  const hostingDomainOk = product?.kind !== 'hosting' || inclCount === 0
    || inclDomains.some(d => (d.domain || '').trim() !== '')

  const entries = product ? pricingEntries(product) : []
  // Tier-priced products (e.g. MailGuard per-mailbox) have empty `pricing`, so
  // fall back to the localized default cycle — otherwise the Select would show
  // the raw value ("monthly") with no matching, translated option.
  const cycleSource = entries.length > 0
    ? entries
    : (product ? [{ cycle: product.default_cycle, price: '' }] : [])
  const availableCycles = cycleSource.map(e => ({
    value: e.cycle,
    label: e.price
      ? `${t(`billingCycle.${e.cycle}`, e.cycle)} — ${fmtPrice(e.price, product!.currency)}`
      : t(`billingCycle.${e.cycle}`, e.cycle),
  }))

  const mailboxes = Number(config[tierKey]) || 0
  const basePrice = useMemo(() => {
    if (isMailboxPricing) return graduatedCharge(mbxTiers, mailboxes)
    const e = entries.find(e => e.cycle === cycle)
    return parseFloat(e?.price || '0')
  }, [entries, cycle, isMailboxPricing, mbxTiers, mailboxes])

  // Live price calculation
  const { optionLines, totalMonthly, totalSetup } = useMemo(() => {
    let total = basePrice * quantity
    const setupBase = parseFloat(product?.setup_fee || '0') * quantity
    let totalSetup = setupBase
    const lines: { label: string; amount: number }[] = []

    for (const opt of opts) {
      const val = config[opt.code]
      if (val === undefined || val === null || val === false || val === '') continue
      const mod = optionPriceModifier(opt, val, basePrice)
      if (mod !== 0) {
        let label = opt.label
        if (opt.kind === 'select') {
          const sv = opt.values?.find(v => v.value === val)
          if (sv) label = `${opt.label}: ${sv.label}`
        } else if ((opt.kind === 'number' || opt.kind === 'slider') && opt.display_format) {
          label = `${opt.label}: ${formatDisplayValue(val, opt.display_format)}`
        }
        lines.push({ label, amount: mod * quantity })
        total += mod * quantity
      }
      totalSetup += parseFloat(opt.price_setup || '0') * quantity
    }

    if (couponApplied) {
      total -= parseFloat(couponApplied.discount)
    }

    return { optionLines: lines, totalMonthly: total, totalSetup }
  }, [basePrice, opts, config, quantity, product, couponApplied])

  const validateCoupon = useMutation({
    mutationFn: async () => {
      if (!product) return null
      const res = await apiClient.post<{ data: { discount_amount: string; coupon: { code: string } } }>(
        '/api/v1/portal/coupons/validate',
        {
          code: couponCode.trim(),
          currency: product.currency,
          subtotal: (basePrice * quantity).toFixed(2),
          product_ids: [product.id],
        },
      )
      return res.data.data
    },
    onSuccess: (d) => {
      if (d) {
        setCouponApplied({ discount: d.discount_amount, code: d.coupon.code })
        void message.success(t('portal.shop.couponApplied', 'Gutschein angewendet'))
      }
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      setCouponApplied(null)
      void message.error(err.response?.data?.error || t('portal.shop.couponInvalid', 'Gutschein ungültig'))
    },
  })

  const cartPath = window.location.pathname.startsWith('/portal') ? '/portal/cart' : '/cart'

  const handleAddToCart = () => {
    if (!product) return
    // SSL: Domain + (bei E-Mail-DCV) Approver-Mail sind im Bestellvorgang Pflicht.
    if (product.kind === 'ssl') {
      if (!((config.ssl_domain as string) || '').trim()) {
        void message.error(t('portal.shop.sslDomainNeeded', 'Bitte die Domain / den Common Name angeben.'))
        return
      }
      if (config.dcv_method === 'email' && !((config.approver_email as string) || '').trim()) {
        void message.error(t('portal.shop.sslApproverNeeded', 'Bitte eine Approver-E-Mail angeben.'))
        return
      }
    }
    // MailGuard-Lizenz: Anzahl Postfächer ist Pflicht (treibt Staffelpreis + Lizenz-Limit).
    if (isMailboxPricing && (Number(config[tierKey]) || 0) < 1) {
      void message.error(t('portal.shop.mbxNeeded', 'Bitte die Anzahl der Postfächer angeben.'))
      return
    }
    // Antispam/Mail-Gateway: Domain + Zielserver sind im Bestellvorgang Pflicht.
    if (isMailgw) {
      if (!((config.domain as string) || '').trim()) {
        void message.error(t('portal.shop.mgwDomainNeeded', 'Bitte die zu schützende Domain angeben.'))
        return
      }
      if (!((config.transport_host as string) || '').trim()) {
        void message.error(t('portal.shop.mgwHostNeeded', 'Bitte den Zielmailserver (Transport-Host) angeben.'))
        return
      }
    }
    // Inklusiv-Domains validieren (Endung erlaubt, Transfer braucht Auth-Code).
    const cleanIncl = inclDomains
      .map(d => ({ ...d, domain: d.domain.trim().toLowerCase() }))
      .filter(d => d.domain !== '')
    for (const d of cleanIncl) {
      // "own": customer manages the domain elsewhere — only used as the webspace
      // domain. No TLD restriction, no auth code, no registration/transfer.
      if (d.action === 'own') {
        if (!d.domain.includes('.')) {
          void message.error(t('portal.shop.ownDomainBad', 'Bitte eine gültige Domain angeben: {{d}}', { d: d.domain }))
          return
        }
        continue
      }
      const tld = d.domain.includes('.') ? d.domain.slice(d.domain.indexOf('.') + 1) : ''
      if (!inclTlds.some(tl => tl.toLowerCase() === tld)) {
        void message.error(t('portal.shop.inclTldBad', 'Endung nicht im Inklusiv-Kontingent: {{d}}', { d: d.domain }))
        return
      }
      if (d.action === 'transfer' && d.auth_code.trim() === '') {
        void message.error(t('portal.shop.inclAuthNeeded', 'Transfer braucht Auth-Code: {{d}}', { d: d.domain }))
        return
      }
      if (d.action === 'register' && d.available === false) {
        void message.error(t('portal.shop.inclTakenErr', 'Domain bereits vergeben — bitte eine andere wählen: {{d}}', { d: d.domain }))
        return
      }
      if (d.action === 'transfer' && d.available === true) {
        void message.error(t('portal.shop.inclNotRegErr', 'Diese Domain ist nicht registriert — Transfer nicht möglich: {{d}}', { d: d.domain }))
        return
      }
    }
    const finalConfig: Record<string, unknown> = { ...config }
    // Only register/transfer domains go to the registrar (inclusive_domains).
    // "own" domains are NOT registered — they just become the webspace domain.
    const toRegister = cleanIncl
      .filter(d => d.action !== 'own')
      .map(d => ({ domain: d.domain, action: d.action, auth_code: d.auth_code }))
    if (toRegister.length > 0) finalConfig.inclusive_domains = toRegister
    // For hosting, the chosen domain (any action — register/transfer/own) IS the
    // site's primary domain → carry it into config.domain so the cart already has
    // it and won't ask again.
    if (product.kind === 'hosting' && !((finalConfig.domain as string) || '').trim() && cleanIncl.length > 0) {
      finalConfig.domain = cleanIncl[0].domain
    }
    // Hosting MUST have a domain — the webspace can't be created without one.
    // Enforced on the product page when the domain block is available (count>0);
    // count==0 hosting still falls back to the cart's domain field.
    if (product.kind === 'hosting' && inclCount > 0 && !((finalConfig.domain as string) || '').trim()) {
      void message.error(t('portal.shop.hostingDomainNeeded', 'Bitte wähle eine Domain für dein Hosting — neu registrieren, umziehen oder eigene Domain.'))
      return
    }
    addItem({
      product_id: product.id,
      slug: product.slug,
      name: product.name,
      kind: product.kind,
      currency: product.currency,
      billing_cycle: cycle,
      quantity,
      config: Object.keys(finalConfig).length > 0 ? finalConfig : {},
      base_price: basePrice,
      setup_fee: parseFloat(product.setup_fee || '0'),
      total_price: totalMonthly / quantity, // per-unit price incl. options
    })
    void message.success(
      <span>
        <strong>{product.name}</strong> {t('portalShop.addedToCart', 'zum Warenkorb hinzugefügt.')}{' '}
        <a onClick={() => navigate(cartPath)} style={{ color: '#3b82f6' }}>{t('portalShop.goToCartLink', 'Zum Warenkorb')}</a>
      </span>,
    )
    close()
  }

  const close = () => {
    setQuantity(1)
    setCouponCode('')
    setCouponApplied(null)
    setConfig({})
    onBack()
  }

  const setVal = (code: string, val: unknown) =>
    setConfig(prev => ({ ...prev, [code]: val }))

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          {t('portal.shop.backToShop', 'Zurück zum Shop')}
        </Button>
        <Typography.Title level={3} style={{ margin: 0 }}>{product.name}</Typography.Title>
      </div>
      <Card className="content-card">
        <div style={{ display: 'flex', gap: 24, flexDirection: screens.md ? 'row' : 'column' }}>

          {/* ── Left: Configurator ── */}
          <div className="flex-1 min-w-0">
            {product.description && (
              <div style={{ marginBottom: 16 }}>
                <ProductDescription text={product.description} />
              </div>
            )}

            {/* Features */}
            {features.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', fontSize: 13 }}>
                {features.map(f => (
                  <li key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span style={{ color: '#10b981' }}>{f.icon || '✓'}</span>
                    {f.label}
                  </li>
                ))}
              </ul>
            )}

            {/* Mindestlaufzeit + Kündigungsfrist */}
            {((product.minimum_term_months || 0) > 0 || (product.cancellation_notice_days || 0) > 0) && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                <Typography.Text style={{ color: '#1d4ed8', fontSize: 13 }}>
                  📋{' '}
                  {(product.minimum_term_months || 0) > 0 && (
                    <><strong>{t('portalShop.minTerm', 'Mindestlaufzeit:')} {product.minimum_term_months! >= 12
                      ? t('portalShop.termYears', '{{n}} Jahr(e) Laufzeit', { n: product.minimum_term_months! / 12 })
                      : t('portalShop.termMonthsFull', '{{n}} Monate', { n: product.minimum_term_months })}</strong>
                    {' '}— {t('portalShop.billingCyclePrefix', 'Abrechnung')} {t(`billingCycle.${cycle}`, cycle)}.{' '}</>
                  )}
                  {(product.cancellation_notice_days || 0) > 0 && (
                    <><strong>{t('portalShop.cancellationNotice', 'Kündigungsfrist:')} {product.cancellation_notice_days} {t('common.days', 'Tage')}</strong>
                    {(product.minimum_term_months || 0) > 0
                      ? t('portalShop.cancellationBeforeEnd', ' vor Laufzeitende.')
                      : t('portalShop.cancellationTimely', ' — Kündigung muss entsprechend frühzeitig eingehen.')}</>
                  )}
                  {(product.minimum_term_months || 0) > 0 && (product.cancellation_notice_days || 0) === 0 && (
                    <> {t('portalShop.cancellationAtEnd', 'Kündigung erst zum Laufzeitende möglich.')}</>
                  )}
                </Typography.Text>
              </div>
            )}

            {/* Cycle + Quantity */}
            <div style={{ marginBottom: 14 }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                {t('portal.shop.cycle', 'Abrechnungszyklus')}
              </Typography.Text>
              <Select className="w-full" value={cycle} options={availableCycles} onChange={setCycle} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                {t('portal.shop.qty', 'Menge')}
              </Typography.Text>
              <InputNumber value={quantity} min={1} max={100}
                onChange={(v) => setQuantity(Number(v) || 1)} style={{ width: '100%' }} />
            </div>

            {/* Options */}
            {opts.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Typography.Text strong style={{ display: 'block', marginBottom: 10 }}>
                  {t('portal.shop.config', 'Konfiguration')}
                </Typography.Text>
                <Space direction="vertical" className="w-full" size={12}>
                  {opts.map(opt => (
                    <OptionField key={opt.code} opt={opt} value={config[opt.code]}
                      basePrice={basePrice} onChange={(v) => setVal(opt.code, v)} />
                  ))}
                </Space>
              </>
            )}

            {/* SSL: Domain + DCV-Methode im Bestellvorgang (Rest danach im Portal) */}
            {product.kind === 'ssl' && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Typography.Text strong style={{ display: 'block', marginBottom: 10 }}>
                  {t('portal.shop.sslConfig', 'SSL-Zertifikat')}
                </Typography.Text>
                <Space direction="vertical" className="w-full" size={12}>
                  <div>
                    <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                      {t('portal.shop.sslDomain', 'Domain / Common Name (CN)')} <span style={{ color: '#ef4444' }}>*</span>
                    </Typography.Text>
                    <Input
                      placeholder="www.example.de"
                      value={(config.ssl_domain as string) || ''}
                      onChange={(e) => setVal('ssl_domain', e.target.value.trim())}
                    />
                  </div>
                  <div>
                    <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                      {t('portal.shop.sslDcv', 'Validierungs-Methode (DCV)')}
                    </Typography.Text>
                    <Select
                      className="w-full"
                      value={(config.dcv_method as string) || 'email'}
                      onChange={(v) => setVal('dcv_method', v)}
                      options={[
                        { value: 'dns', label: t('portal.shop.dcvDns', 'DNS (CNAME-Eintrag)') },
                        { value: 'http', label: t('portal.shop.dcvHttp', 'HTTP (Datei auf Server)') },
                        { value: 'email', label: t('portal.shop.dcvEmail', 'E-Mail an Approver-Adresse') },
                      ]}
                    />
                  </div>
                  {config.dcv_method === 'email' && (
                    <div>
                      <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                        {t('portal.shop.sslApprover', 'Approver-E-Mail')} <span style={{ color: '#ef4444' }}>*</span>
                      </Typography.Text>
                      <Input
                        placeholder="admin@example.de"
                        value={(config.approver_email as string) || ''}
                        onChange={(e) => setVal('approver_email', e.target.value.trim())}
                      />
                    </div>
                  )}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('portal.shop.sslLater', 'CSR und Validierung erfolgen nach dem Kauf im Kundenportal unter „SSL".')}
                  </Typography.Text>
                </Space>
              </>
            )}

            {/* S3-Objektspeicher: Region im Bestellvorgang wählen */}
            {isS3 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Typography.Text strong style={{ display: 'block', marginBottom: 10 }}>
                  {t('portal.shop.s3Config', 'Objektspeicher')}
                </Typography.Text>
                <div>
                  <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                    {t('portal.shop.s3Region', 'Region')}
                  </Typography.Text>
                  <Select
                    className="w-full"
                    value={(config.region as string) || 'eu-central-2'}
                    onChange={(v) => setVal('region', v)}
                    loading={regionsQ.isLoading}
                    options={s3Regions.map(r => ({ value: r.value, label: r.label }))}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('portal.shop.s3RegionHint', 'Speicherort deiner Daten. Nach dem Kauf nicht mehr änderbar.')}
                  </Typography.Text>
                </div>
              </>
            )}

            {/* Lizenz mit Mengen-Staffel: Anzahl (Postfächer/Nodes/…) → Staffelpreis + Lizenz-Limit */}
            {isMailboxPricing && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Typography.Text strong style={{ display: 'block', marginBottom: 10 }}>
                  {t('portal.shop.mbxConfig', 'Lizenz-Umfang')}
                </Typography.Text>
                <div>
                  <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                    {t('portal.shop.qtyCount', 'Anzahl {{unit}}', { unit: tierUnitLabel(tierKey).many })} <span style={{ color: '#ef4444' }}>*</span>
                  </Typography.Text>
                  <InputNumber className="w-full" min={1} step={1}
                    value={(config[tierKey] as number) ?? 1}
                    onChange={(v) => setVal(tierKey, v ?? 1)} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('portal.shop.qtyHint', 'Du zahlst pro {{unit}} (Staffelpreis – mehr {{unit}}, günstiger pro Stück). Alle übrigen Limits sind unbegrenzt. Der Preis oben aktualisiert sich automatisch.', { unit: tierUnitLabel(tierKey).many })}
                  </Typography.Text>
                </div>
              </>
            )}

            {/* SaaS metered: Postfächer werden gemessen → Staffel nur als Preis-Info */}
            {isMeteredMailboxes && mbxTiers.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {t('portal.shop.meteredTitle', 'Preis je Postfach (monatlich)')}
                </Typography.Text>
                <table style={{ width: '100%', maxWidth: 380, fontSize: 13, borderCollapse: 'collapse' }}>
                  <tbody>
                    {mbxTiers.map((tier, i) => {
                      const lower = i === 0 ? 1 : ((mbxTiers[i - 1].up_to_gb ?? 0) + 1)
                      const upper = tier.up_to_gb
                      const range = upper == null
                        ? t('portal.shop.meteredFrom', 'ab {{n}} Postfächer', { n: lower })
                        : `${lower}–${upper} ${t('portal.shop.mbxUnit', 'Postfächer')}`
                      const price = `${tier.price_per_gb.toFixed(2).replace('.', ',')} €`
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--nc-border, #f0f0f0)' }}>
                          <td style={{ padding: '4px 8px 4px 0' }}>{range}</td>
                          <td style={{ padding: '4px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {price}<Typography.Text type="secondary"> / {t('portal.shop.perMbxMonth', 'Postfach·Monat')}</Typography.Text>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                  {t('portal.shop.meteredHint', 'Zusätzlich zum Grundpreis. Abgerechnet wird monatlich nach der tatsächlichen Zahl deiner Postfächer (Monats-Höchstwert). Staffel: jedes Postfach wird nach seiner Stufe berechnet — mehr Postfächer, günstiger pro Stück. Die Postfächer pflegst du nach der Bestellung im Kundenbereich.')}
                </Typography.Text>
              </>
            )}

            {/* Antispam / Mail-Gateway: Domain + Zielserver im Bestellvorgang */}
            {isMailgw && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Typography.Text strong style={{ display: 'block', marginBottom: 10 }}>
                  {t('portal.shop.mgwConfig', 'Antispam-Konfiguration')}
                </Typography.Text>
                <Space direction="vertical" className="w-full" size={12}>
                  <div>
                    <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                      {t('portal.shop.mgwDomain', 'Zu schützende Domain')} <span style={{ color: '#ef4444' }}>*</span>
                    </Typography.Text>
                    <Input placeholder="example.de"
                      value={(config.domain as string) || ''}
                      onChange={(e) => setVal('domain', e.target.value.trim().toLowerCase())} />
                  </div>
                  <Row gutter={12}>
                    <Col span={16}>
                      <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                        {t('portal.shop.mgwHost', 'Zielmailserver (wohin gefilterte Mails gehen)')} <span style={{ color: '#ef4444' }}>*</span>
                      </Typography.Text>
                      <Input placeholder="mail.example.de"
                        value={(config.transport_host as string) || ''}
                        onChange={(e) => setVal('transport_host', e.target.value.trim())} />
                    </Col>
                    <Col span={8}>
                      <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
                        {t('portal.shop.mgwPort', 'Port')}
                      </Typography.Text>
                      <InputNumber className="w-full" placeholder="25"
                        value={(config.transport_port as number) ?? 25}
                        onChange={(v) => setVal('transport_port', v ?? 25)} />
                    </Col>
                  </Row>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('portal.shop.mgwHint', 'Eingehende Mail wird über das Antispam-Gateway gefiltert und an deinen Zielserver zugestellt. IMAP-Self-Service, Sandbox & Relay-Account stellst du nach der Bestellung im Kundenbereich ein.')}
                  </Typography.Text>
                </Space>
              </>
            )}

            {/* Inklusiv-Domains: im Paket enthaltene kostenlose Domains beim Bestellen erfassen */}
            {inclCount > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <GiftOutlined style={{ color: '#7c3aed', fontSize: 16 }} />
                  <Typography.Text strong>
                    {t('portal.shop.inclTitle2', 'Gratis-Domain im Paket')} ({inclDomains.length}/{inclCount})
                  </Typography.Text>
                </div>
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
                  {t('portal.shop.inclHint2', 'Wähle deine kostenlose Domain — neu registrieren oder umziehen. Erlaubte Endungen:')}{' '}
                  {inclTlds.map(tl => <Tag key={tl} color="purple">.{tl}</Tag>)}
                </Typography.Text>
                <Space direction="vertical" className="w-full" size={8}>
                  {inclDomains.map((d, i) => {
                    const dom = (d.domain || '').trim().toLowerCase()
                    const resolved = !d.checking && d.available != null && dom !== ''
                    const ok = d.action === 'register' ? d.available === true : d.available === false
                    return (
                      <div key={i} style={{
                        border: `1px solid ${resolved ? (ok ? '#86efac' : '#fca5a5') : '#e5e7eb'}`,
                        background: resolved ? (ok ? '#f0fdf4' : '#fef2f2') : '#fafafa',
                        borderRadius: 10, padding: 12, transition: 'border-color .15s, background .15s',
                      }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                          <Input
                            prefix={<GlobalOutlined style={{ color: '#94a3b8' }} />}
                            style={{ flex: screens.md ? '0 0 220px' : '1 1 100%', minWidth: 0 }}
                            placeholder="meinedomain.de"
                            value={d.domain}
                            onChange={(e) => setInclDomains(arr => arr.map((x, j) => j === i ? { ...x, domain: e.target.value, available: null } : x))}
                            onBlur={() => checkInclDomain(i)}
                            onPressEnter={() => checkInclDomain(i)}
                          />
                          <Segmented
                            block={!screens.md}
                            style={{ flex: screens.md ? '0 0 auto' : '1 1 100%' }}
                            value={d.action}
                            onChange={(v) => setInclDomains(arr => arr.map((x, j) => j === i ? { ...x, action: v as 'register' | 'transfer' | 'own', available: null } : x))}
                            options={[
                              { value: 'register', label: t('portal.shop.inclRegister3', 'Registrieren') },
                              { value: 'transfer', label: t('portal.shop.inclTransfer2', 'Umziehen') },
                              { value: 'own', label: t('portal.shop.inclOwn2', 'Eigene') },
                            ]}
                          />
                          {d.action === 'transfer' && (
                            <Input
                              prefix={<KeyOutlined style={{ color: '#94a3b8' }} />}
                              style={{ flex: screens.md ? '0 0 180px' : '1 1 100%', minWidth: 0 }}
                              placeholder={t('portal.shop.inclAuth', 'Auth-Code')}
                              value={d.auth_code}
                              onChange={(e) => setInclDomains(arr => arr.map((x, j) => j === i ? { ...x, auth_code: e.target.value } : x))}
                            />
                          )}
                          <div style={{ display: 'flex', gap: 4, marginLeft: screens.md ? 'auto' : 0 }}>
                            {d.action !== 'own' && (
                              <Button type="primary" ghost size="small" loading={d.checking} icon={<SearchOutlined />}
                                onClick={() => checkInclDomain(i)}>
                                {t('portal.shop.inclCheck', 'Prüfen')}
                              </Button>
                            )}
                            <Button type="text" danger size="small" icon={<DeleteOutlined />}
                              onClick={() => setInclDomains(arr => arr.filter((_, j) => j !== i))} />
                          </div>
                        </div>
                        {d.action === 'own' && (d.domain || '').trim() !== '' && (
                          <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <InfoCircleOutlined style={{ color: '#2563eb', fontSize: 16, marginTop: 2 }} />
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {t('portal.shop.inclOwnHint', 'Du verwaltest diese Domain selbst (DNS/Registrar woanders). Wir legen nur den Webspace dafür an — bitte richte nach der Bestellung den A-/CNAME-Record auf unseren Server.')}
                            </Typography.Text>
                          </div>
                        )}
                        {d.action !== 'own' && resolved && (
                          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {ok
                              ? <CheckCircleFilled style={{ color: '#16a34a', fontSize: 18 }} />
                              : <CloseCircleFilled style={{ color: '#dc2626', fontSize: 18 }} />}
                            <Typography.Text strong style={{ color: ok ? '#15803d' : '#b91c1c' }}>
                              {d.action === 'register'
                                ? (ok
                                    ? t('portal.shop.inclFreeMsg', '{{d}} ist verfügbar — im Paket inklusive (0 €)', { d: dom })
                                    : t('portal.shop.inclTakenMsg', '{{d}} ist leider schon vergeben', { d: dom }))
                                : (ok
                                    ? t('portal.shop.inclRegMsg', '{{d}} ist registriert — Umzug möglich, inklusive', { d: dom })
                                    : t('portal.shop.inclNotRegMsg', '{{d}} ist nicht registriert — Umzug nicht möglich', { d: dom }))}
                            </Typography.Text>
                          </div>
                        )}
                      </div>
                    )
                  })}
                  {inclDomains.length < inclCount && (
                    <Button type="dashed" block icon={<PlusOutlined />}
                      onClick={() => setInclDomains(arr => [...arr, { domain: '', action: 'register', auth_code: '' }])}>
                      {t('portal.shop.inclAdd', 'Inklusiv-Domain hinzufügen')}
                    </Button>
                  )}
                </Space>
              </>
            )}

            {/* Coupon */}
            <Divider style={{ margin: '12px 0' }} />
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                {t('portal.shop.coupon', 'Gutschein-Code')}
              </Typography.Text>
              <Space.Compact className="w-full">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="PROMO2026"
                  disabled={!!couponApplied}
                  onPressEnter={() => !couponApplied && couponCode.trim() && validateCoupon.mutate()}
                />
                {couponApplied ? (
                  <Button onClick={() => { setCouponApplied(null); setCouponCode('') }}>
                    {t('portal.shop.couponClear', 'Entfernen')}
                  </Button>
                ) : (
                  <Button onClick={() => validateCoupon.mutate()} loading={validateCoupon.isPending}
                    disabled={!couponCode.trim()}>
                    {t('portal.shop.couponApply', 'Einlösen')}
                  </Button>
                )}
              </Space.Compact>
              {couponApplied && (
                <Typography.Text style={{ color: '#10b981', fontSize: 13, display: 'block', marginTop: 4 }}>
                  <CheckCircleOutlined /> Rabatt: −{couponApplied.discount} {product.currency}
                </Typography.Text>
              )}
            </div>
          </div>

          {/* ── Right: Price summary ── */}
          <div style={{ width: screens.md ? 300 : '100%', flexShrink: 0 }}>
            <div style={{
              background: '#f8fafc', borderRadius: 10, padding: '18px 20px',
              border: '1px solid #e5e7eb', position: 'sticky', top: 0,
            }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 10, fontSize: 13,
                textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>
                {t('portalShop.priceSummary', 'Preisübersicht')}
              </Typography.Text>

              <div className="shop-price-row shop-price-row--total">
                <span>{t(`billingCycle.${cycle}`, cycle)}</span>
                <span>{fmtPrice(basePrice * quantity, product.currency)}</span>
              </div>

              {optionLines.map((line, i) => (
                <div key={i} className="shop-price-row shop-price-row--sub">
                  <span className="shop-price-label">{line.label}</span>
                  <span>{line.amount >= 0 ? '+' : ''}{fmtPrice(line.amount, product.currency)}</span>
                </div>
              ))}

              {couponApplied && (
                <div className="shop-price-row shop-price-row--disc">
                  <span>{t('portalShop.discount', 'Rabatt')}</span>
                  <span>−{couponApplied.discount} {product.currency}</span>
                </div>
              )}

              {totalSetup > 0 && (
                <div className="shop-price-row shop-price-row--fee">
                  <span>{t('portalShop.setupFee', 'Einrichtung')}</span>
                  <span>{fmtPrice(totalSetup, product.currency)}</span>
                </div>
              )}

              <Divider className="divider-compact" />

              {(() => {
                const rate = Number(taxQ.data?.rate_pct ?? '0')
                const taxMonthly = totalMonthly * rate / 100
                const grossMonthly = totalMonthly + taxMonthly
                const reason = taxQ.data?.reason
                const ratePretty = rate % 1 === 0 ? rate.toFixed(0) : String(rate)
                return (
                  <>
                    <div className="shop-price-row">
                      <span>{t('portalShop.net', 'Netto')}</span>
                      <span>{fmtPrice(totalMonthly, product.currency)}</span>
                    </div>
                    {taxQ.isLoading ? (
                      <Typography.Text type="secondary" className="text-xs d-block mt-4">
                        {t('portalShop.vatLoading', 'zzgl. gesetzl. MwSt. …')}
                      </Typography.Text>
                    ) : rate > 0 ? (
                      <>
                        <div className="shop-price-row shop-price-row--sub">
                          <span className="shop-price-label">{t('portalShop.vatRate', 'zzgl. MwSt. ({{rate}}%)', { rate: ratePretty })}</span>
                          <span>{fmtPrice(taxMonthly, product.currency)}</span>
                        </div>
                        <Divider className="divider-compact" />
                        <div className="flex-between font-bold">
                          <span>{t('portalShop.grossTotal', 'Gesamt (brutto)')}</span>
                          <span className="text-primary">{fmtPrice(grossMonthly, product.currency)}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <Divider className="divider-compact" />
                        <div className="flex-between font-bold">
                          <span>{t('portalShop.total', 'Gesamt')}</span>
                          <span className="text-primary">{fmtPrice(totalMonthly, product.currency)}</span>
                        </div>
                        <Typography.Text type="secondary" className="text-xs d-block mt-4">
                          {reason === 'reverse_charge'
                            ? t('portalShop.taxReverseCharge', 'Reverse Charge — Steuerschuldnerschaft des Leistungsempfängers (0 % USt.)')
                            : reason === 'kleinunternehmer'
                              ? t('portalShop.taxKleinunternehmer', 'Kein Ausweis von USt. (Kleinunternehmer §19 UStG)')
                              : reason === 'customer_exempt'
                                ? t('portalShop.taxExempt', 'Steuerbefreit')
                                : t('portalShop.taxZero', 'inkl. 0 % USt.')}
                        </Typography.Text>
                      </>
                    )}
                  </>
                )
              })()}
              {totalSetup > 0 && (
                <Typography.Text type="secondary" className="text-xs d-block mt-4">
                  + {fmtPrice(totalSetup, product.currency)} {t('portalShop.setupFeeOnce', 'Einrichtung (einmalig, zzgl. MwSt.)')}
                </Typography.Text>
              )}
            </div>
          </div>
        </div>
        {!hostingDomainOk && (
          <Alert type="warning" showIcon style={{ marginTop: 16 }}
            message={t('portal.shop.hostingDomainHint', 'Bitte oben eine Domain für dein Hosting wählen (neu registrieren, umziehen oder eigene Domain), um fortzufahren.')} />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid var(--nc-border, #e5e7eb)', marginTop: 20, paddingTop: 16, flexWrap: 'wrap' }}>
          <Button onClick={onBack}>{t('common.cancel', 'Abbrechen')}</Button>
          <Button icon={<ShoppingCartOutlined />} disabled={!hostingDomainOk}
            onClick={() => { handleAddToCart(); navigate(cartPath) }}>
            {t('portal.shop.goToCart', 'Zur Kasse')}
          </Button>
          <Button type="primary" icon={<ShoppingCartOutlined />} disabled={!hostingDomainOk}
            onClick={handleAddToCart}>
            {t('portal.shop.addToCart', 'In den Warenkorb')}
          </Button>
        </div>
      </Card>
    </>
  )
}

// ── OptionField ───────────────────────────────────────────────────────────────

function OptionField({
  opt, value, basePrice, onChange,
}: {
  opt: ProductOption
  value: unknown
  basePrice: number
  onChange: (v: unknown) => void
}) {
  const { t } = useTranslation()
  const mod = optionPriceModifier(opt, value, basePrice)
  const modLabel = mod !== 0
    ? <Tag color={mod > 0 ? 'blue' : 'green'} style={{ fontSize: 11 }}>
        {mod > 0 ? '+' : ''}{mod.toFixed(2)} €
      </Tag>
    : null

  const label = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
      <Typography.Text strong style={{ fontSize: 13 }}>
        {opt.label}
        {opt.required && <Typography.Text type="danger"> *</Typography.Text>}
      </Typography.Text>
      {modLabel}
      {opt.help_text && (
        <Tooltip title={opt.help_text}>
          <InfoCircleOutlined style={{ color: '#94a3b8', fontSize: 13 }} />
        </Tooltip>
      )}
    </div>
  )

  const cfg = opt.config || {}

  if (opt.kind === 'boolean') {
    return (
      <div>
        {label}
        <Space>
          <Switch checked={!!value} onChange={onChange} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {value ? t('portalShop.optionEnabled', 'Aktiviert') : t('portalShop.optionDisabled', 'Deaktiviert')}
          </Typography.Text>
        </Space>
      </div>
    )
  }

  if (opt.kind === 'select') {
    const selectOpts = (opt.values || []).map(v => {
      const vMod = parseFloat(v.price_modifier || '0')
      const modStr = vMod !== 0
        ? ` (+${vMod.toFixed(2)} €)`
        : ''
      return { value: v.value, label: `${v.label}${modStr}` }
    })
    return (
      <div>
        {label}
        <Select className="w-full" value={value as string | undefined}
          options={selectOpts} onChange={onChange}
          placeholder={t('portalShop.selectPlaceholder', 'Auswählen…')} />
      </div>
    )
  }

  if (opt.kind === 'slider') {
    const min = cfg.min ?? 0
    const max = cfg.max ?? 100
    const step = cfg.step ?? 1
    return (
      <div>
        {label}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Slider className="flex-1" min={min} max={max} step={step}
            value={value as number ?? min}
            onChange={onChange} />
          <Typography.Text style={{ minWidth: 60, textAlign: 'right' }}>
            {formatDisplayValue(value ?? min, opt.display_format)}
          </Typography.Text>
        </div>
      </div>
    )
  }

  if (opt.kind === 'number') {
    const min = cfg.min ?? 0
    const max = cfg.max
    const step = cfg.step ?? 1
    return (
      <div>
        {label}
        <InputNumber className="w-full" min={min} max={max} step={step}
          value={value as number | undefined}
          onChange={onChange}
          formatter={opt.display_format
            ? (v) => formatDisplayValue(v, opt.display_format)
            : undefined}
          parser={opt.display_format
            ? (v) => Number((v || '').replace(/[^0-9.]/g, ''))
            : undefined}
        />
      </div>
    )
  }

  // text
  return (
    <div>
      {label}
      <Input value={value as string | undefined}
        onChange={(e) => onChange(e.target.value)}
        placeholder={opt.help_text || ''} />
    </div>
  )
}
